import {
  useRef,
  useState,
  useEffect,
  createRef,
  RefObject,
  useCallback,
  useMemo,
} from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import {
  Dropdown,
  tailwindOverride,
  Tooltip,
  Checkbox,
} from '@getsynapse/design-system';
import { Option } from 'utils/customTypes';
import {
  getProjectSummaries,
  projectSummarySelectors,
} from 'state/normalized/projectSummaries/projectSummariesSlice';
import { formatProjectNumber } from 'utils/formatters';
import { naturalSort } from 'utils/naturalSorting';
import { getInitialValueForDropDown } from 'utils/functions';

type ProjectsDropdownProps = {
  onChange: (options: Option[]) => void;
  values: string[];
};

const ProjectsDropdown = ({ onChange, values }: ProjectsDropdownProps) => {
  const dispatch = useDispatch();
  const projectRefs = useRef<RefObject<HTMLLIElement>[]>([]);

  const projects = useSelector(projectSummarySelectors.selectAll);
  const canLoadMore = useSelector(projectSummarySelectors.selectCanFetchMore);

  const [truncatedItems, setTruncatedItems] = useState<boolean[]>([]);
  const [dropdownClicks, setDropdownClicks] = useState(0);
  const [updateTruncatedList, setUpdateTruncatedList] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const projectOptions = useMemo<Option[]>(() => {
    const options = projects?.map((project) => ({
      label: `${formatProjectNumber(project.projectNumber)} - ${project.title}`,
      value: project.id,
    }));
    return naturalSort(options, 'label', 'asc');
  }, [projects]);

  useEffect(() => {
    if (dropdownClicks === 1) setTruncatedItems([]);
  }, [dropdownClicks]);

  const selectedValues = getInitialValueForDropDown(projectOptions, values);

  useEffect(() => {
    const findProjects = debounce(async () => {
      dispatch(
        getProjectSummaries({
          search: searchValue || undefined,
        })
      );
      setUpdateTruncatedList(true);
    }, 300);

    findProjects();

    return () => {
      findProjects.cancel();
    };
  }, [searchValue, dispatch]);

  useEffect(() => {
    projectRefs.current = projectOptions?.map(
      (_, i) => projectRefs.current[i] ?? createRef()
    );
  }, [projectOptions]);

  useEffect(() => {
    if (updateTruncatedList) {
      requestAnimationFrame(() => {
        const truncationStates = projectRefs.current.map((ref) => {
          const item = ref.current;
          return item ? item.scrollWidth > item.clientWidth : false;
        });
        setTruncatedItems(truncationStates);
        setUpdateTruncatedList(false);
      });
    }
  }, [updateTruncatedList]);

  const getProjectTitle = useCallback(
    (value: string) => projects.find((project) => project.id === value)?.title,
    [projects]
  );

  const renderOption = useCallback(
    (
      option: Option,
      selected: boolean,
      selectOption: (option: Option) => void,
      { className, ...otherProps }
    ) => {
      const index = projectOptions.findIndex(
        (opt) => opt.value === option.value
      );
      const isCurrentItemTruncated = truncatedItems[index];

      const optionItem = (
        <li
          {...otherProps}
          ref={projectRefs.current[index]}
          className={tailwindOverride('flex justify-between	group', className, {
            'hover:bg-secondary-lightest hover:shadow-list-item-hover-green':
              !selected,
            'hover:bg-primary focus-visible:bg-primary': selected,
          })}
          onClick={() => {
            selectOption(option);
            setSearchValue('');
          }}
        >
          <Checkbox
            checked={selected}
            label={option.label}
            id='projects-dropdown-checkbox'
            inputProps={{
              className: tailwindOverride('mt-0', {
                'group-hover:border-neutral-white group-focus-visible:border-neutral-white':
                  selected,
                'group-hover:border-secondary-dark group-focus-visible:border-secondary-dark':
                  !selected,
              }),
            }}
            className={tailwindOverride('inline-block', {
              'group-hover:text-secondary-darker group-focus-visible:text-secondary-darker':
                !selected,
              'group-hover:text-neutral-white group-focus-visible:text-neutral-white':
                selected,
              truncate: isCurrentItemTruncated,
            })}
          />
        </li>
      );

      return isCurrentItemTruncated ? (
        <Tooltip
          className='w-full'
          ariaId='dropdown-tooltip'
          openMode='hover1'
          timeout={0}
          trigger={optionItem}
          usePortal={true}
          showPopper={true}
          contentProps={{ className: 'z-high text-sm' }}
        >
          {getProjectTitle(option.value)}
        </Tooltip>
      ) : (
        optionItem
      );
    },
    [projectOptions, truncatedItems, getProjectTitle]
  );

  const loadMoreOptions = useCallback(async () => {
    if (canLoadMore) {
      await dispatch(
        getProjectSummaries({
          search: searchValue || undefined,
          fetchNext: true,
        })
      );
      setUpdateTruncatedList(true);
    }
  }, [dispatch, searchValue, canLoadMore]);

  const handleDropdownClick = () => {
    if (dropdownClicks === 0) {
      setUpdateTruncatedList(true);
    }
    setDropdownClicks((prevState) => prevState + 1);
  };

  return (
    <Dropdown
      onClick={handleDropdownClick}
      values={selectedValues}
      className='w-128'
      enableLazyLoading
      loadMoreOptions={loadMoreOptions}
      onClose={() => {
        if (searchValue) setSearchValue('');
        setDropdownClicks(0);
      }}
      onChange={onChange}
      options={projectOptions}
      filterable
      multiple
      triggerProps={{
        'data-testid': 'projects-dropdown-trigger',
      }}
      listProps={{
        'data-testid': 'projects-dropdown-list',
      }}
      renderOption={renderOption}
      placeholder={intl.get(
        'SCHEDULE.EVENTS.DETAILS_SECTION.PROJECT_PLACEHOLDER'
      )}
      onFilterChange={(value) => setSearchValue(value)}
    />
  );
};

export default ProjectsDropdown;
